import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FileWarning, RefreshCw, Search, Filter, Download, Eye, Trash2, Clock, TestTube2, CheckCircle2, XCircle } from 'lucide-react';

function fmtDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch { return iso; }
}

export default function PdfErrors() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [retentionDays, setRetentionDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState('30');
  const [endpointFilter, setEndpointFilter] = useState('all'); // all|preventivo|contratto
  const [selected, setSelected] = useState(null); // errore selezionato per stack trace
  const [exporting, setExporting] = useState(false);
  const [testUrl, setTestUrl] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { days: parseInt(days, 10) || 30, limit: 300 };
      if (endpointFilter === 'preventivo') params.endpoint = 'preventivo';
      if (endpointFilter === 'contratto') params.endpoint = 'contratto';
      const r = await api.listPdfErrors(params);
      setItems(r.items || []);
      setTotal(r.total || 0);
      setRetentionDays(r.retention_days || 30);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossibile caricare gli errori PDF');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = { days: parseInt(days, 10) || 30 };
      if (endpointFilter === 'preventivo') params.endpoint = 'preventivo';
      if (endpointFilter === 'contratto') params.endpoint = 'contratto';
      const blob = await api.downloadPdfErrorsCsv(params);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pdf-errors-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success('Export CSV completato');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossibile esportare il CSV');
    } finally { setExporting(false); }
  };

  const handleTestCloudinary = async () => {
    if (!testUrl.trim()) { toast.error('Incolla un URL Cloudinary'); return; }
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.testCloudinaryPdf(testUrl.trim());
      setTestResult(r);
      if (r.ok) toast.success(`PDF scaricato correttamente (${(r.size/1024).toFixed(1)} KB)`);
      else toast.error(r.message || 'Download fallito');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore durante il test');
    } finally { setTesting(false); }
  };

  return (
    <div>
      <PageHeader
        title="Log Errori PDF"
        description={`Registro degli errori di generazione PDF (preventivi + contratti). Retention: ultimi ${retentionDays} giorni. Visibile solo al Super Admin.`}
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <Card data-testid="pdf-errors-total">
          <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">Errori nel periodo</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-semibold flex items-center gap-2"><FileWarning className="size-5 text-destructive" /> {total}</div></CardContent>
        </Card>
        <Card data-testid="pdf-errors-retention">
          <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">Retention</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-semibold flex items-center gap-2"><Clock className="size-5 text-primary" /> {retentionDays} giorni</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">Ultimo</CardTitle></CardHeader>
          <CardContent><div className="text-sm">{items[0] ? fmtDate(items[0].created_at) : '—'}</div></CardContent>
        </Card>
      </div>

      <Card className="mb-4">
        <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2"><Filter className="size-4" /> Filtri</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Endpoint</Label>
            <Select value={endpointFilter} onValueChange={setEndpointFilter}>
              <SelectTrigger className="w-full" data-testid="pdf-errors-filter-endpoint"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli endpoint</SelectItem>
                <SelectItem value="preventivo">Solo Preventivo</SelectItem>
                <SelectItem value="contratto">Solo Contratto</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Ultimi giorni</Label>
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="w-full" data-testid="pdf-errors-filter-days"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Ultimo giorno</SelectItem>
                <SelectItem value="7">Ultimi 7 giorni</SelectItem>
                <SelectItem value="30">Ultimi 30 giorni</SelectItem>
                <SelectItem value="90">Ultimi 90 giorni</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2 md:col-span-2">
            <Button onClick={load} variant="outline" data-testid="pdf-errors-refresh" disabled={loading}>
              <RefreshCw className={`size-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Aggiorna
            </Button>
            <Button onClick={handleExport} disabled={exporting || items.length === 0} data-testid="pdf-errors-export-csv">
              <Download className="size-4 mr-2" /> Export CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-4" data-testid="pdf-cloudinary-test-card">
        <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2"><TestTube2 className="size-4" /> Test scheda tecnica Cloudinary</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Incolla qui l'URL di una scheda tecnica caricata su Cloudinary per verificare se il sistema riesce a scaricarla. Applica tutti i fallback (raw, fl_attachment, rimozione trasformazioni).
            Se il test fallisce, apri il tuo pannello Cloudinary e abilita <strong>Settings → Security → Media Delivery → Allow delivery of PDF and ZIP files</strong>.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              value={testUrl}
              onChange={(e) => setTestUrl(e.target.value)}
              placeholder="https://res.cloudinary.com/.../file.pdf"
              className="font-mono text-xs"
              data-testid="pdf-cloudinary-test-input"
            />
            <Button onClick={handleTestCloudinary} disabled={testing || !testUrl.trim()} data-testid="pdf-cloudinary-test-button">
              {testing ? <RefreshCw className="size-4 mr-2 animate-spin" /> : <TestTube2 className="size-4 mr-2" />} Testa URL
            </Button>
          </div>
          {testResult && (
            <div className={`rounded-md border p-3 text-sm ${testResult.ok ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-destructive/40 bg-destructive/5'}`} data-testid="pdf-cloudinary-test-result">
              <div className="flex items-start gap-2">
                {testResult.ok ? <CheckCircle2 className="size-4 text-emerald-600 mt-0.5" /> : <XCircle className="size-4 text-destructive mt-0.5" />}
                <div className="flex-1">
                  <div className="font-medium">{testResult.ok ? `PDF scaricato · ${(testResult.size/1024).toFixed(1)} KB` : 'URL non deliverable come PDF binario'}</div>
                  <div className="text-xs text-muted-foreground mt-1">{testResult.message}</div>
                </div>
              </div>
              {testResult.strict && (
                <div className="mt-3 pt-3 border-t grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="font-medium mb-1 flex items-center gap-1">
                      {testResult.strict.ok ? <CheckCircle2 className="size-3 text-emerald-600" /> : <XCircle className="size-3 text-destructive" />}
                      URL diretto (secure_url salvato in DB)
                    </div>
                    <div className="text-muted-foreground">HTTP {testResult.strict.http_status || 'n/a'} · Content-Type: <code>{testResult.strict.content_type || 'n/a'}</code></div>
                    <div className="text-muted-foreground">Magic bytes PDF: {testResult.strict.is_pdf ? '✓ %PDF-' : '✗ non è un PDF'}</div>
                  </div>
                  {testResult.with_fallbacks && (
                    <div>
                      <div className="font-medium mb-1 flex items-center gap-1">
                        {testResult.with_fallbacks.ok ? <CheckCircle2 className="size-3 text-emerald-600" /> : <XCircle className="size-3 text-destructive" />}
                        Con fallback (fl_attachment, raw, ecc.)
                      </div>
                      <div className="text-muted-foreground">{testResult.with_fallbacks.ok ? `${(testResult.with_fallbacks.size/1024).toFixed(1)} KB scaricati` : 'Nessun fallback riuscito'}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-sm">Elenco errori ({items.length})</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-sm text-muted-foreground py-6 text-center">Caricamento…</div>
          ) : items.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">Nessun errore registrato nel periodo selezionato. Ottimo! 🎉</div>
          ) : (
            <div className="overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data / Ora</TableHead>
                    <TableHead>Endpoint</TableHead>
                    <TableHead>Utente</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Messaggio</TableHead>
                    <TableHead className="w-[80px]">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it) => (
                    <TableRow key={it.id} data-testid={`pdf-error-row-${it.id}`}>
                      <TableCell className="text-xs tabular-nums whitespace-nowrap">{fmtDate(it.created_at)}</TableCell>
                      <TableCell className="text-xs font-mono">{it.endpoint || '—'}</TableCell>
                      <TableCell className="text-xs">
                        <div>{it.user_email || '—'}</div>
                        {it.user_role && <Badge variant="secondary" className="text-[10px] mt-0.5">{it.user_role}</Badge>}
                      </TableCell>
                      <TableCell>
                        <Badge variant="destructive" className="text-[10px]">{it.error_type || 'Error'}</Badge>
                      </TableCell>
                      <TableCell className="text-xs max-w-[380px] truncate" title={it.error_message}>{it.error_message}</TableCell>
                      <TableCell>
                        <Button size="sm" variant="ghost" onClick={() => setSelected(it)} data-testid={`pdf-error-view-${it.id}`}>
                          <Eye className="size-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-4xl" data-testid="pdf-error-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileWarning className="size-4 text-destructive" /> Dettagli errore PDF</DialogTitle>
            <DialogDescription>
              {selected && (
                <span>{fmtDate(selected.created_at)} · {selected.endpoint} · <Badge variant="destructive" className="ml-1">{selected.error_type}</Badge></span>
              )}
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Messaggio</Label>
                <div className="text-sm font-mono mt-1 p-3 rounded-md bg-secondary/40">{selected.error_message}</div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Stack trace</Label>
                <ScrollArea className="h-[280px] w-full mt-1 rounded-md border">
                  <pre className="text-[11px] font-mono p-3 whitespace-pre-wrap">{selected.stack_trace || '—'}</pre>
                </ScrollArea>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Utente</Label>
                  <div className="mt-1">{selected.user_email || '—'} <span className="text-muted-foreground">({selected.user_role || 'n/a'})</span></div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Company ID</Label>
                  <div className="mt-1 font-mono">{selected.company_id || '—'}</div>
                </div>
              </div>
              {selected.payload_meta && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Payload (ridotto)</Label>
                  <ScrollArea className="h-[200px] w-full mt-1 rounded-md border">
                    <pre className="text-[11px] font-mono p-3 whitespace-pre-wrap">{JSON.stringify(selected.payload_meta, null, 2)}</pre>
                  </ScrollArea>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
